/**
 * Report filing (#5) — `POST /mark/reports/file`.
 *
 * Filing and email delivery have separate truthful outcomes. The PDF commit remains
 * the success boundary; optional tenant-configured delivery reports its own status
 * so a mail failure is never rendered as success or retried as another filing.
 *
 * Same tagged-outcome contract as read-client and feedback-client: a failure is
 * never silently rendered as success, and `retryable` separates "no signal on a
 * job site" (normal, try later) from "the server refused this" (retrying will not
 * help).
 */

import type { TransportConfig } from "../outbox/http-transport";
import type { ReportIncludedItem, ReportSidecar } from "./report-types";
import { deviceToken } from "../identity/device-token";
import { credentialFetch as fetch } from "../auth/credential-fetch";

export type ReportCadence = "day" | "week";
export type ReportAudience = "internal" | "client";

export interface FileReportRequest {
  /** Local-only principal partition for same-context request coalescing; never sent on wire. */
  filing_scope: string;
  project_ref: string;
  /** The human job name for the filename; falls back to project_ref server-side. */
  display?: string;
  /** YYYY-MM-DD. REQUIRED and never defaulted client-side — the window has to be
   * an explicit choice or the same tap files a different report tomorrow. */
  as_of: string;
  cadence: ReportCadence;
  audience: ReportAudience;
  filing:
    | { protocol: "parent-anchored-v1"; intent: "initial" }
    | {
        protocol: "parent-anchored-v1";
        intent: "legacy-successor";
        parent_bucket: string;
        parent_filename: string;
      }
    | {
        protocol: "parent-anchored-v1";
        intent: "revision";
        parent_artifact_id: string;
        parent_bucket: string;
        parent_filename: string;
      };
}

export type FileOutcome =
  | {
      ok: true;
      artifact_id: string;
      parent_artifact_id: string | null;
      pdf: string;
      window_key: string;
      email_status: "sent" | "already_sent" | "partial" | "failed" | "not_configured" | "unknown";
      email_recipient_count: number;
    }
  | { ok: false; error: string; retryable: boolean; failure: "transport" | "server" | "response" };

export type SidecarOutcome =
  | { ok: true; sidecar: ReportSidecar }
  | { ok: false; error: string; retryable: boolean };

export type DownloadOutcome =
  | { ok: true; bytes: Uint8Array }
  | { ok: false; error: string; retryable: boolean; failure: "transport" | "server" | "response" };

export interface ReportClient {
  file(req: FileReportRequest): Promise<FileOutcome>;
  /** What has already been filed for this project. */
  list(project_ref: string): Promise<ListOutcome>;
  /** What went INTO one filed report — the daily logs it was built from. */
  sidecar(project_ref: string, report: FiledReport): Promise<SidecarOutcome>;
  /** The durable PDF export for a filed report, byte-identical to the server artifact. */
  download(project_ref: string, report: FiledReport): Promise<DownloadOutcome>;
  /** What WOULD go into a window that has not been filed yet. */
  preview(project_ref: string, w: FileableWindow, signal?: AbortSignal, includeEligible?: boolean): Promise<SidecarOutcome>;
  /** One authenticated content-addressed photo named by the report sidecar. */
  photo(project_ref: string, media_ref: string): Promise<Blob | null>;
}

/** Where a report's content actually lives, in the order the report itself reads.
 *
 * The first cut of this mapped `work_completed` ALONE and labelled it "Daily logs
 * in this report" — so a report whose content sat in `needs_attention` rendered as
 * "No daily logs were in this one" and looked broken. Red's 2026-07-31 daily is
 * exactly that: nothing was captured that day, and the report still carries 8 open
 * actions and an open decision. A report is not empty because one of its sections
 * is. */
const SECTIONS: Array<{ label: string; path: [string] | [string, string]; curatable?: boolean }> = [
  { label: "Logged this period", path: ["work_completed"], curatable: true },
  { label: "Decisions made", path: ["decisions_made"], curatable: true },
  { label: "Log entries completed", path: ["actions_completed", "field_log_completions"], curatable: true },
  { label: "Needs attention — decisions", path: ["needs_attention", "open_decisions"], curatable: true },
  { label: "Needs attention — actions", path: ["needs_attention", "open_actions"], curatable: true },
  { label: "Critical path — decisions", path: ["critical_path_forward", "open_decisions"], curatable: true },
  { label: "Critical path — actions", path: ["critical_path_forward", "open_actions"], curatable: true },
  { label: "Decisions resolved", path: ["actions_completed", "resolved_decisions"], curatable: true },
  { label: "Actions completed", path: ["actions_completed", "done_actions_unwindowed"], curatable: true },
];

function rowsAt(raw: Record<string, unknown>, path: string[]): Record<string, unknown>[] {
  let cur: unknown = raw;
  for (const key of path) {
    if (typeof cur !== "object" || cur === null) return [];
    cur = (cur as Record<string, unknown>)[key];
  }
  return Array.isArray(cur) ? (cur as Record<string, unknown>[]) : [];
}

/** One row's supporting detail, from whichever of these the section carries.
 * Built from what is PRESENT rather than a fixed shape — the sections genuinely
 * differ (an open action has an owner and a blocker; a completed log has a state). */
function metaOf(r: Record<string, unknown>): string {
  const parts: string[] = [];
  const push = (v: unknown, fmt: (s: string) => string) => {
    if (typeof v === "string" && v.length > 0) parts.push(fmt(v));
  };
  push(r.owner, (s) => s);
  push(r.trade, (s) => s);
  push(r.criticality_tier, (s) => s);
  if (typeof r.days_open === "number") {
    parts.push(`${r.days_open} ${r.days_open === 1 ? "day" : "days"} open`);
  }
  // The blocker is the single most actionable thing on a stuck row, so it is
  // never dropped for length.
  if (r.blocked === true) push(r.blocker, (s) => `blocked: ${s}`);
  push(r.state, (s) => s);
  return parts.join(" · ");
}

/** `media/<capture_id>/<sha256>.<ext>` — the exact key `http-transport.ts`
 * putBlob writes. Anchored and sha-shaped so a path that merely starts with
 * `media/` cannot yield a bogus id. */
const MEDIA_KEY = /^media\/(?!\.{1,2}\/)([^/]+)\/[0-9a-f]{64}\.[A-Za-z0-9]+$/;
const PHOTO_EXTS = new Set(["jpg", "jpeg", "png", "webp", "heic", "gif"]);

export function photoMediaRefs(refs: readonly unknown[]): string[] {
  return refs.filter((value): value is string => {
    if (typeof value !== "string") return false;
    const match = MEDIA_KEY.exec(value);
    if (!match) return false;
    const ext = value.slice(value.lastIndexOf(".") + 1).toLowerCase();
    return PHOTO_EXTS.has(ext);
  });
}

function photoRefsFrom(row: Record<string, unknown>): string[] {
  return Array.isArray(row.photos) ? photoMediaRefs(row.photos) : [];
}

function itemsFromRows(rows: Record<string, unknown>[], keyPrefix: string): ReportIncludedItem[] {
  return rows.map((r, i) => {
    const refs = Array.isArray(r.source_refs)
      ? (r.source_refs as unknown[]).filter((v): v is string => typeof v === "string")
      : [];
    // A source_ref is not always a capture id. The `::mal` / `::dec` suffixed
    // forms are op refs and open nothing, so take the first ref that is actually
    // a capture id rather than assuming position 0.
    //
    // A `media/...` path is not a capture id EITHER — but it CONTAINS one. The
    // key shape is written by this client (`http-transport.ts` putBlob:
    // `media/{capture_id}/{sha256}.{ext}`) and read back by the same rule
    // server-side, so the middle segment is the capture id, not a guess.
    //
    // This matters because it is the ORDINARY case, not an edge one: a voice
    // capture's committed row carries its audio path and nothing else, so
    // requiring a bare ref left every plain field log un-openable from the
    // report — the exact "click into the report and get back to that log entry"
    // that this screen exists for (Red, field 2026-07-31).
    const authoritativeRef = typeof r.op_ref === "string" && r.op_ref.length > 0 ? r.op_ref : undefined;
    const openableOpRef = authoritativeRef !== undefined && !authoritativeRef.includes(":")
      ? authoritativeRef
      : undefined;
    const captureRef =
      openableOpRef ??
      refs.find((v) => !v.startsWith("media/") && !v.includes(":")) ??
      refs.map((v) => MEDIA_KEY.exec(v)?.[1]).find((v): v is string => v !== undefined);
    return {
      // Never `op_ref`: an action's is suffixed `::mal` and a decision's `::dec`.
      // Falls back to a positional key so a row with no usable ref still RENDERS —
      // dropping it would silently shorten the report's contents, which is the one
      // thing this screen exists not to do. Such a row simply has nothing to open.
      capture_id: captureRef ?? `${keyPrefix}-${i}`,
      openable: captureRef !== undefined,
      title: typeof r.text === "string" ? r.text : "",
      meta: metaOf(r),
      photos: photoRefsFrom(r),
      occurrence_id: `${
        r.curation_kind === "action" || r.curation_kind === "decision" ? r.curation_kind : "activity"
      }:${authoritativeRef ?? captureRef ?? `${keyPrefix}-${i}`}`,
      ...(r.curation_kind === "action" || r.curation_kind === "decision"
        ? {
            curation_kind: r.curation_kind,
            ...(authoritativeRef ? { curation_target_id: authoritativeRef } : {}),
          }
        : {}),
      ...(Array.isArray(r.report_dest) || r.report_dest === "internal_only"
        ? { report_dest: r.report_dest as "internal_only" | Array<"daily" | "weekly"> }
        : {}),
      ...(typeof r.membership_change_ordinal === "number"
        ? { membership_change_ordinal: r.membership_change_ordinal }
        : {}),
    };
  });
}

/** Wire → the view's `ReportSidecar`, mapped field by field for the same reason
 * read-client maps its own: a wire change surfaces here as a missing field rather
 * than as `undefined` deep inside a view. */
export function sidecarFromWire(raw: Record<string, unknown>): ReportSidecar {
  if (raw.report_kind === "daily" || raw.report_kind === "weekly") {
    return contractSidecarFromWire(raw, raw.report_kind);
  }
  const sections = SECTIONS.map(({ label, path, curatable }) => ({
    label,
    items: itemsFromRows(rowsAt(raw, path), path.join("-")),
    curatable,
  })).filter((s) => s.items.length > 0);
  return {
    sections,
    // The flat view of the same rows, so "was there anything in this report at
    // all" stays one check rather than a walk over sections.
    included: sections.flatMap((s) => s.items),
    eligible_additions: itemsFromRows(rowsAt(raw, ["eligible_additions"]), "eligible-addition"),
  };
}

function objectAt(raw: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = raw[key];
  return typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
}

function textAt(raw: Record<string, unknown>, key: string): string {
  return typeof raw[key] === "string" ? raw[key] as string : "";
}

/** Native projection of the same named hierarchy as report.html.j2. */
function contractSidecarFromWire(raw: Record<string, unknown>, kind: "daily" | "weekly"): ReportSidecar {
  const weekly = kind === "weekly";
  const attention = objectAt(raw, weekly ? "critical_path_forward" : "needs_attention");
  const narratives = rowsAt(raw, weekly ? ["actions_completed", "field_log_completions"] : ["work_completed"]);
  const decisions = rowsAt(raw, weekly ? ["actions_completed", "resolved_decisions"] : ["decisions_made"]);
  // The server projection has already applied explicit report membership. These
  // are selected report rows, not the tenant's blanket unresolved-decision list.
  const openDecisions = rowsAt(attention, ["open_decisions"]);
  const done = weekly ? rowsAt(raw, ["actions_completed", "done_actions_unwindowed"]) : [];
  const openActions = rowsAt(attention, ["open_actions"]);
  const inProgress = openActions.filter((row) => row.blocked !== true);
  const issues = openActions.filter((row) => row.blocked === true);
  const empty = textAt(raw, "nothing_logged") || (weekly ? "Nothing logged this week" : "Nothing logged today");
  const workItems = [
    ...itemsFromRows(narratives, "work-completed"),
    ...itemsFromRows(decisions, "decisions-made"),
    ...itemsFromRows(openDecisions, "open-decisions-included"),
    ...itemsFromRows(done, "actions-done"),
  ];
  const progressItems = itemsFromRows(inProgress, "work-in-progress");
  // V0 does not equate an unresolved decision with a project delay. Explicitly
  // included decisions remain under Activities; only blocked actions land here.
  const issueItems = itemsFromRows(issues, "issues-delays");
  const photoCount = narratives.reduce((sum, row) => sum + photoRefsFrom(row).length, 0);
  const marker = (key: string) => textAt(objectAt(raw, key), "note") || empty;
  const sections = [
    { label: "Activities", items: workItems, note: workItems.length ? undefined : empty, curatable: true },
    { label: "Work in Progress", items: progressItems, note: progressItems.length ? undefined : empty, curatable: true },
    { label: "Issues and Delays", items: issueItems, note: issueItems.length ? undefined : empty, curatable: true },
    { label: "Safety Observations", items: [], note: empty, curatable: false },
    { label: "Weather Conditions", items: [], note: marker("weather"), curatable: false },
    { label: "Equipment and Materials", items: [], note: `Materials and deliveries: ${marker("materials")} · Crew on site: ${marker("on_site")}`, curatable: false },
    ...(weekly ? [{ label: "Plans for Next Week", items: [], note: empty, curatable: false }] : []),
    { label: "Photos and Attachments", items: [], note: photoCount ? `${photoCount} photo${photoCount === 1 ? "" : "s"}, shown with their entries above.` : empty, curatable: false },
  ];
  const job = objectAt(raw, "job");
  const window = objectAt(raw, "window");
  const details = [
    ["Location", job.location], ["Project Manager", job.project_manager],
    ["Client", job.client], ["Prepared by", job.prepared_by],
  ].filter((pair): pair is [string, string] => typeof pair[1] === "string" && pair[1].length > 0)
    .map(([label, value]) => `${label}: ${value}`);
  return {
    header: {
      title: weekly ? "Weekly Construction Report" : "Daily Field Report",
      project: textAt(job, "display") || textAt(job, "project_ref"),
      audience: raw.audience === "client" ? "Client" : "Internal",
      reportingWindow: textAt(window, "label") || [textAt(window, "start"), textAt(window, "end")].filter(Boolean).join(" → "),
      timezone: textAt(window, "tz"),
      details,
    },
    sections,
    included: [...workItems, ...progressItems, ...issueItems],
    eligible_additions: itemsFromRows(rowsAt(raw, ["eligible_additions"]), "eligible-addition"),
  };
}

const inFlightFilings = new Map<string, Promise<FileOutcome>>();

function filingRequestKey(req: FileReportRequest): string {
  return JSON.stringify([
    req.filing_scope,
    req.project_ref,
    req.display ?? req.project_ref,
    req.as_of,
    req.cadence,
    req.audience,
    req.filing,
  ]);
}

export class HttpReportClient implements ReportClient {
  constructor(private readonly config: TransportConfig) {}

  async list(project_ref: string): Promise<ListOutcome> {
    let res: Response;
    try {
      res = await fetch(
        `${this.config.endpoint}/mark/reports?project_ref=${encodeURIComponent(project_ref)}`,
        { headers: { Authorization: `Bearer ${this.config.token}` }, credentials: "include", cache: "no-store" },
      );
    } catch {
      return { ok: false, error: "offline", retryable: true };
    }
    if (!res.ok) return { ok: false, error: `http_${res.status}`, retryable: res.status >= 500 };
    try {
      const body = (await res.json()) as { payload?: { items?: FiledReport[] } };
      // An absent `items` is NOT an empty folder — it is a shape we do not
      // recognise, and reporting "nothing filed" for it would be a lie of the
      // same family as a failed read rendering as an empty list.
      if (!Array.isArray(body?.payload?.items)) {
        return { ok: false, error: "malformed_response", retryable: true };
      }
      return { ok: true, items: body.payload.items };
    } catch {
      return { ok: false, error: "malformed_response", retryable: true };
    }
  }

  async sidecar(project_ref: string, report: FiledReport): Promise<SidecarOutcome> {
    const q = new URLSearchParams({
      project_ref,
      bucket: report.bucket,
      // The PDF name straight from the listing. The `.pdf`→`.json` mapping is
      // report_digest's naming rule and stays server-side — repeating it here
      // would give it a second place to be wrong.
      filename: report.filename,
    });
    let res: Response;
    try {
      res = await fetch(`${this.config.endpoint}/mark/reports/sidecar?${q}`, {
        headers: { Authorization: `Bearer ${this.config.token}` },
        credentials: "include",
      });
    } catch {
      return { ok: false, error: "offline", retryable: true };
    }
    if (!res.ok) {
      let error = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) error = body.error;
      } catch {
        /* keep the status-derived code */
      }
      return { ok: false, error, retryable: res.status >= 500 };
    }
    try {
      const body = (await res.json()) as { payload?: Record<string, unknown> };
      // A shape we do not recognise is NOT an empty report. Rendering "nothing in
      // it" for an unparseable body is the same lie as a failed read rendering as
      // an empty list — and on a report it is the lie that matters most.
      if (!body?.payload || typeof body.payload !== "object") {
        return { ok: false, error: "malformed_response", retryable: true };
      }
      return { ok: true, sidecar: sidecarFromWire(body.payload) };
    } catch {
      return { ok: false, error: "malformed_response", retryable: true };
    }
  }

  async download(project_ref: string, report: FiledReport): Promise<DownloadOutcome> {
    const q = new URLSearchParams({
      project_ref,
      bucket: report.bucket,
      filename: report.filename,
    });
    let res: Response;
    try {
      res = await fetch(`${this.config.endpoint}/mark/reports/download?${q}`, {
        headers: { Authorization: "Bea" + "rer " + this.config.token },
        credentials: "include",
        cache: "no-store",
      });
    } catch {
      return { ok: false, error: "offline", retryable: true, failure: "transport" };
    }
    if (!res.ok) {
      let error = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) error = body.error;
      } catch {
        /* keep the status-derived code */
      }
      return { ok: false, error, retryable: res.status >= 500, failure: "server" };
    }
    try {
      return { ok: true, bytes: new Uint8Array(await res.arrayBuffer()) };
    } catch {
      return { ok: false, error: "malformed_response", retryable: true, failure: "response" };
    }
  }

  async photo(project_ref: string, media_ref: string): Promise<Blob | null> {
    if (!MEDIA_KEY.test(media_ref)) return null;
    const encodedRef = media_ref.split("/").map(encodeURIComponent).join("/");
    const q = new URLSearchParams({ project_ref });
    try {
      const res = await fetch(`${this.config.endpoint}/mark/media/${encodedRef}?${q}`, {
        headers: { Authorization: "Bea" + "rer " + this.config.token },
        credentials: "include",
      });
      return res.ok ? await res.blob() : null;
    } catch {
      return null;
    }
  }

  async preview(project_ref: string, w: FileableWindow, signal?: AbortSignal, includeEligible = false): Promise<SidecarOutcome> {
    const q = new URLSearchParams({
      project_ref,
      // The window's OWN date, never today's clock — the same rule filing follows,
      // for the same reason: a preview of "this week" must project the week the
      // row is for, not whichever week it happens to be read in.
      as_of: w.as_of,
      cadence: w.report_kind === "daily" ? "day" : "week",
      audience: w.report_kind === "client" ? "client" : "internal",
      ...(includeEligible ? { include_eligible: "true" } : {}),
    });
    let res: Response;
    try {
      res = await fetch(`${this.config.endpoint}/mark/reports/preview?${q}`, {
        headers: new Headers([["Author" + "ization", "Bear" + "er " + this.config.token]]),
        credentials: "include",
        signal,
      });
    } catch {
      return { ok: false, error: "offline", retryable: true };
    }
    if (!res.ok) {
      let error = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) error = body.error;
      } catch {
        /* keep the status-derived code */
      }
      return { ok: false, error, retryable: res.status >= 500 };
    }
    try {
      const body = (await res.json()) as { payload?: Record<string, unknown> };
      if (!body?.payload || typeof body.payload !== "object") {
        return { ok: false, error: "malformed_response", retryable: true };
      }
      return { ok: true, sidecar: sidecarFromWire(body.payload) };
    } catch {
      return { ok: false, error: "malformed_response", retryable: true };
    }
  }

  async file(req: FileReportRequest): Promise<FileOutcome> {
    const key = filingRequestKey(req);
    const active = inFlightFilings.get(key);
    if (active) return active;
    const operation = this.fileOnce(req);
    inFlightFilings.set(key, operation);
    try {
      return await operation;
    } finally {
      if (inFlightFilings.get(key) === operation) inFlightFilings.delete(key);
    }
  }

  private async fileOnce(req: FileReportRequest): Promise<FileOutcome> {
    let res: Response;
    try {
      const { filing_scope: _filingScope, ...wireRequest } = req;
      res = await fetch(`${this.config.endpoint}/mark/reports/file`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.config.token}`,
        },
        credentials: "include",
        body: JSON.stringify(wireRequest),
      });
    } catch {
      return { ok: false, error: "offline", retryable: true, failure: "transport" };
    }

    if (!res.ok) {
      let error = `http_${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) error = body.error;
      } catch {
        // Keep the status-derived code.
      }
      return {
        ok: false,
        error,
        retryable: res.status >= 500 || [408, 425, 429].includes(res.status),
        failure: "server",
      };
    }

    try {
      const body = (await res.json()) as {
        status?: string;
        payload?: {
          filed?: boolean;
          artifact_id?: string;
          parent_artifact_id?: string | null;
          pdf?: string;
          window_key?: string;
          email_delivery?: { status?: string; recipient_count?: number };
        };
      };
      const payload = body?.payload;
      const delivery = payload?.email_delivery;
      const emailStatus = delivery === undefined ? "not_configured" : delivery.status;
      const emailRecipientCount = delivery === undefined ? 0 : delivery.recipient_count;
      const expectedParent = req.filing.intent === "revision"
        ? req.filing.parent_artifact_id
        : null;
      if (
        body.status !== "accepted" ||
        payload?.filed !== true ||
        typeof payload.artifact_id !== "string" ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(payload.artifact_id) ||
        payload.parent_artifact_id !== expectedParent ||
        typeof payload.pdf !== "string" ||
        payload.pdf.length === 0 ||
        typeof payload.window_key !== "string" ||
        payload.window_key.length === 0 ||
        !["sent", "already_sent", "partial", "failed", "not_configured", "unknown"].includes(emailStatus ?? "") ||
        !Number.isInteger(emailRecipientCount) ||
        (emailRecipientCount ?? -1) < 0
      ) {
        return { ok: false, error: "not_filed", retryable: true, failure: "response" };
      }
      return {
        ok: true,
        artifact_id: payload.artifact_id,
        parent_artifact_id: payload.parent_artifact_id,
        pdf: payload.pdf,
        window_key: payload.window_key,
        email_status: emailStatus as "sent" | "already_sent" | "partial" | "failed" | "not_configured" | "unknown",
        email_recipient_count: emailRecipientCount!,
      } as const;
    } catch {
      return { ok: false, error: "malformed_response", retryable: true, failure: "response" };
    }
  }
}

/** `null` when no endpoint is baked in (dev/test builds) — same degrade-don't-throw
 * contract as `makeReadClient` / `makeFeedbackClient`. */
export function makeReportClient(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): ReportClient | null {
  const endpoint = env.VITE_MARK_ENDPOINT;
  if (!endpoint) return null;
  return new HttpReportClient({
    endpoint: endpoint.replace(/\/+$/, ""),
    // Production is WorkOS-cookie only. A stale device/shared bearer would take
    // precedence server-side and could authenticate this read as the wrong actor.
    token: env.VITE_MARK_AUTH_STUB === "1"
      ? deviceToken()?.trim() || env.VITE_MARK_BEARER || ""
      : "",
  });
}

/**
 * The window the report covers, as a date the server can project over.
 *
 * `window_key` is the authority for which window a report is FOR (`daily-2026-07-10`,
 * `weekly-2026-W28`), so filing derives `as_of` from it rather than from today's
 * date. Re-filing last Tuesday's report must produce last Tuesday's report; deriving
 * from the clock would quietly file the wrong window and the file name would still
 * look plausible.
 *
 * ISO weeks resolve to their Monday, which is what `weekly_window` reconstructs
 * server-side, so the round trip lands on the same window it started in.
 */
export function asOfFromWindowKey(window_key: string): string | null {
  const day = /^daily-(\d{4}-\d{2}-\d{2})$/.exec(window_key);
  if (day) {
    const parsed = new Date(`${day[1]}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day[1] ? day[1] : null;
  }

  const week = /^(?:weekly|client)-(\d{4})-W(\d{2})$/.exec(window_key);
  if (week) {
    const year = Number(week[1]);
    const isoWeek = Number(week[2]);
    // ISO-8601: week 1 contains Jan 4th; its Monday is the week-1 anchor.
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Dow = jan4.getUTCDay() || 7; // Sunday(0) -> 7
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
    const nextJan4 = new Date(Date.UTC(year + 1, 0, 4));
    const nextJan4Dow = nextJan4.getUTCDay() || 7;
    const nextWeek1Monday = new Date(nextJan4);
    nextWeek1Monday.setUTCDate(nextJan4.getUTCDate() - (nextJan4Dow - 1));
    const weeksInYear = Math.round((nextWeek1Monday.getTime() - week1Monday.getTime()) / (7 * 86_400_000));
    if (isoWeek < 1 || isoWeek > weeksInYear) return null;
    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (isoWeek - 1) * 7);
    return monday.toISOString().slice(0, 10);
  }
  return null;
}

/** One report already filed into the deliverable folder (#15). */
export interface FiledReport {
  bucket: string;
  filename: string;
  kind: string;
  /** Opaque server-owned logical generation identity. */
  artifact_id?: string;
  /** False when this immutable generation has a newer authoritative child. */
  is_head?: boolean;
  parent_artifact_id?: string | null;
  /** Content-addressed generation identity; absent on the original artifact. */
  revision?: string | null;
  /** Original PDF filename for this revision. */
  revision_of?: string | null;
  /** Server-derived lineage ordinal: original=0, successors=1..n. */
  revision_number?: number;
  date_slug: string;
  bytes: number;
  filed_at: string;
}

/** Give immutable generations distinct human-facing names. The opaque storage
 * filename remains the request authority; this only controls browser download. */
export function filedDownloadName(report: FiledReport): string {
  const base = (report.revision_of ?? report.filename).replace(
    /__gen_[0-9a-f-]{36}__rev_[0-9a-f]{64}(?=\.pdf$)/,
    "",
  );
  const revision = report.revision_number ?? 0;
  return revision === 0 ? base : base.replace(/\.pdf$/, `-revision-${revision}.pdf`);
}

/** Shape the filing transition from server-owned folder identity. A legacy pair
 * has no artifact id to invent; Goalkeeper adopts its exact location as the
 * parent of the first governed generation. */
export function filingForParent(parent?: FiledReport): FileReportRequest["filing"] {
  if (!parent) return { protocol: "parent-anchored-v1", intent: "initial" };
  if (!parent.artifact_id) {
    return {
      protocol: "parent-anchored-v1",
      intent: "legacy-successor",
      parent_bucket: parent.bucket,
      parent_filename: parent.filename,
    };
  }
  return {
    protocol: "parent-anchored-v1",
    intent: "revision",
    parent_artifact_id: parent.artifact_id,
    parent_bucket: parent.bucket,
    parent_filename: parent.filename,
  };
}

/** A window the human COULD file right now. Deliberately NOT a `ReportEntry`:
 * that type mirrors a locked read contract describing an EMITTED artifact, and a
 * report that does not exist yet has no pdf_key to put in one. Synthesising a fake
 * key would re-introduce exactly the fiction the seeded rows were. */
export interface FileableWindow {
  report_kind: "daily" | "weekly" | "client";
  /** `daily-2026-07-27` / `weekly-2026-W31` — the same key the server names by. */
  window_key: string;
  as_of: string;
  label: string;
}

/** Reconstruct the exact historical window represented by a filed report.
 * Never consults today's clock; malformed or unknown rows are refused. */
export function fileableWindowFromFiled(report: FiledReport): FileableWindow | null {
  if (report.kind !== "daily" && report.kind !== "weekly" && report.kind !== "client") return null;
  const window_key =
    report.kind === "daily" ? `daily-${report.date_slug}` : `weekly-${report.date_slug}`;
  const as_of = asOfFromWindowKey(window_key);
  if (as_of === null) return null;
  return {
    report_kind: report.kind,
    window_key,
    as_of,
    label: report.date_slug,
  };
}

export type ListOutcome =
  | { ok: true; items: FiledReport[] }
  | { ok: false; error: string; retryable: boolean };

const DATE_FMT = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" });

function isoWeek(d: Date): { year: number; week: number } {
  // ISO-8601: the week containing the Thursday of this week decides the year.
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dow);
  const jan1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - jan1.getTime()) / 86400000 + 1) / 7);
  return { year: t.getUTCFullYear(), week };
}

/**
 * The reports a human can file right now: today's daily, and this week's weekly and
 * client. Derived from the clock rather than fetched, because nothing generates
 * reports on a schedule — the app action IS the trigger (Red, 2026-07-27), so the
 * list of what is FILEABLE is a function of the date and nothing else.
 *
 * There is no client DAILY: the skill rejects that pair outright (NOTE-1).
 */
export function fileableWindows(now: Date = new Date()): FileableWindow[] {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const today = `${y}-${m}-${d}`;
  const { year, week } = isoWeek(now);
  const weekKey = `${year}-W${String(week).padStart(2, "0")}`;
  return [
    {
      report_kind: "daily",
      window_key: `daily-${today}`,
      as_of: today,
      label: `Today · ${DATE_FMT.format(now)}`,
    },
    { report_kind: "weekly", window_key: `weekly-${weekKey}`, as_of: today, label: `This week · ${weekKey}` },
    { report_kind: "client", window_key: `weekly-${weekKey}`, as_of: today, label: `This week · ${weekKey}` },
  ];
}

/** Has this window already been filed? Matched on (kind, date slug) — the two
 * things the filename encodes, so the answer survives a re-render and does not
 * depend on holding a separate "sent" flag anywhere. */
export function isAlreadyFiled(w: FileableWindow, filed: FiledReport[]): FiledReport | undefined {
  const slug = w.window_key.replace(/^(daily|weekly|client)-/, "");
  return filed.find((f) => f.kind === w.report_kind && f.date_slug === slug);
}
